import {
  OrderRefundReasons,
  RefundTypes,
} from '@lambda/commons/apis/elasticsearch/types';
import {
  ReebeloCurrencies,
  ReebeloSalesChannelT,
  ReebeloStoreT,
} from '@lambda/reebelo';

export enum OrderInvoiceType {
  STANDARD = 'standard',
  BUYER_PROTECTION_FEE = 'buyer-protection-fee',
  SELLER_INVOICE = 'seller-invoice',
  /** One per order, covering every seller's lines (RT-17241). */
  PROOF_OF_PURCHASE = 'proof-of-purchase',
}

export type OrderTimeline = {
  createdAt: string;
  description: string;
  id: string;
  orderId: string;
  context: Record<string, any>;
  updatedAt: string;
};

export type OrderTimelineResponse = {
  count: number;
  lastKey?: string;
  data: OrderTimeline[];
};

export type Risk = {
  createdAt: string; // ISO timestamp for when the risk analysis was created
  highRisk: boolean; // Indicates whether the transaction is high risk
  vendor: string; // Vendor performing the risk analysis (e.g., 'PAYPAL')
  updatedAt: string; // ISO timestamp for the last update of the risk analysis
};

export type OrderTransaction = {
  id: string; // Unique identifier for the transaction (e.g., 'txn-...')
  orderId: string; // Associated order ID (e.g., 'ord-...')
  type: string; // Transaction type (e.g., 'AUTHORIZATION')
  status: string; // Transaction status (e.g., 'SUCCESS')
  gateway: string; // Payment gateway used (e.g., 'paypal')
  gatewayId: string; // ID specific to the payment gateway
  amount: number; // Transaction amount in the smallest currency unit (e.g., cents for CAD)
  currency: string; // Currency code (e.g., 'CAD')
  createdAt: string; // ISO timestamp for when the transaction was created
  updatedAt: string; // ISO timestamp for when the transaction was last updated
  risk: Risk; // Risk analysis details
};

export type OrderTransactionsResponse = {
  count: number; // Number of transactions
  lastKey?: string; // Last key for pagination
  data: OrderTransaction[]; // Array of order transactions
};

export type Fraud = {
  createdAt: string;
  updatedAt: string;
  vendor: 'SIGNIFYD' | 'RISKIFIED';
  decision: string;
  externalId: string;
  shouldCapture: boolean;
  context: Record<string, any>;
};

enum AddressType {
  BILLING = 'billing',
  SHIPPING = 'shipping',
}

type AddressDTO = {
  type: AddressType;
  firstName: string;
  lastName: string;
  phone: string;
  company: string;
  address1: string;
  address2: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
};

export enum RefundStatus {
  OPEN = 'open',
  CANCELLED = 'cancelled',
  APPROVED = 'approved',
  REJECTED = 'rejected',
  PROCESSING = 'processing',
  FAILED = 'failed',
  CLOSED = 'closed',
}

export type RefundAmount = {
  currency: ReebeloCurrencies;
  subTotal: number;
  taxTotal: number;
  dutiesTotal: number;
  discountTotal: number;
  shippingTotal: number;
  grandTotal: number;
};

export type RefundNote = {
  name: string;
  value: string;
};

export type RefundLineItemDetails = {
  orderLineId: string;
  amount: RefundAmount;
  vendorId?: string;
  vendorSku?: string;
  quantity?: number;
};

export type RefundDetails = {
  zendeskId?: string;
  status: RefundStatus;
  type: RefundTypes;
  currency: ReebeloCurrencies;
  createdBy: string;
  reasonCode: OrderRefundReasons;
  lines: RefundLineItemDetails[];
  notes?: RefundNote[];
};

export type UpdateOrderDTO = {
  customer?: UpdateOrderCustomerDTO;
  lines?: UpdateOrderLineDTO[];
};

export type UpdateOrderCustomerDTO = {
  billingAddress?: AddressDTO;
  shippingAddress?: AddressDTO;
  email?: string;
};

export type UpdateOrderLineDTO = {
  id?: string;
  reebeloId?: string;
  imeis?: string[];
};

type AmountDTO = {
  currency: string;
  subTotal: number;
  taxTotal: number;
  dutiesTotal: number;
  discountTotal: number;
  shippingTotal: number;
  grandTotal: number;
};

type OrderSalesChannelDTO = {
  name: ReebeloSalesChannelT;
  orderId: string;
  context: Record<string, any>;
};

type OrderCustomerDTO = {
  customerId: string;
  email: string;
  billingAddress: AddressDTO;
  shippingAddress: AddressDTO;
};

type OrderSessionDTO = {
  sessionId: string;
  anonymousId: string;
  checkoutId: string;
  ipAddress: string;
  userAgent: string;
};

type OrderFraudDTO = {
  vendor: string;
  descision: string;
  externalId: string;
  shouldCapture: boolean;
  createdAt: string;
  updatedAt: string;
};

type OrderCancellationDTO = {
  cancelledAt: string;
  reason: string;
  context: Record<string, any>;
};

type OrderNoteDTO = {
  name: string;
  value: string;
};

type OrderDiscountDTO = {
  source: string;
  code: string;
  amount: number;
  externalId: string;
};

type OrderTaxDTO = {
  title: string;
  rate: number;
  price: number;
};

export type OrderLineDTO = {
  id: string;
  externalId: number;
  skuId?: string;
  type: string;
  typeId: string;
  shopifyVariantId: number;
  vendorId: string;
  vendorSku: string;
  psku: string;
  category: { pk?: string; id: string; name: string };
  variants: { name: string; handle: string; value: string }[];
  quantity: number;
  taxable: boolean;
  price: number;
  amount: AmountDTO;
  title: string;
  addons: OrderLineDTO[];
  images: { url: string }[];
  invoice: InvoiceDTO | null;
  notes: OrderNoteDTO[];
};

export type OrderDTO = {
  id: string;
  externalId: string;
  store: string;
  shopifyId: string;
  shopifyName: string;
  createdAt: string;
  updatedAt: string;
  status: string;
  amount: AmountDTO;
  salesChannel: OrderSalesChannelDTO;
  customer: OrderCustomerDTO;
  session: OrderSessionDTO;
  fraud: OrderFraudDTO;
  cancellation: OrderCancellationDTO;
  notes: OrderNoteDTO[];
  discounts: OrderDiscountDTO[];
  taxes: OrderTaxDTO[];
  duties: OrderTaxDTO[];
  lines: OrderLineDTO[];
  shopifyEnabled: boolean;
  orderName: string;
  reebeloId?: string;
};

export type InvoiceDTO = {
  id: string;
  url?: string;
  createdAt?: string;
  updatedAt?: string;
};

export type OrderInvoiceDTO = {
  id: string;
  orderId: string;
  url: string;
  vendorName?: string;
  type?: OrderInvoiceType;
  createdAt: string;
  updatedAt: string;
};

export type SearchOrdersResponseDTO = {
  count: number;
  lastKey: any;
  data: OrderDTO[];
};

export enum InvoiceRequestStatus {
  PENDING = 'pending',
  UPLOADED = 'uploaded',
  CANCELLED = 'cancelled',
}

/**
 * One outstanding request for a seller to supply an invoice.
 *
 * `vendorId` is `${store}#${vendor}` — both the Cobalt vendor claim and the
 * prefix of the ES order doc id, so `${vendorId}#${orderNumber}` addresses the
 * exact ES document this request belongs to.
 */
export type InvoiceRequestDTO = {
  vendorId: string;
  orderNumber: number;
  orderId: string;
  type: OrderInvoiceType;
  createdAt: string;
  dueAt: string;
};

/** Scoping params shared by the invoice-request list and count endpoints. */
export type InvoiceRequestQuery = {
  status?: InvoiceRequestStatus;
  lateOnly?: boolean;
  store: ReebeloStoreT;
  vendorId?: string;
  customerId?: string;
};
